import { forkJoin, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { MIHttpClientService } from '../shared/mi-http-client.service';
import {
  ICalendarEventType,
  ICalendarFilters,
  IOutOfOffice,
  IOutOfOfficeType,
  IScheduledVisit,
  IStoreVisitEventType,
  MICalendarEvent,
  MICalendarEventTypesEnum
} from './calendar';
import { mergeMap, take } from 'rxjs/operators';
import { IJob } from '../batches/jobs';
import { HelpersService } from '../shared/helpers.service';
import { IStore } from '../stores/store';
import { PermissionService } from '../core/permission.service';
import moment from 'moment';
import { IdName } from '../shared/common-interfaces';
import { GroupsService } from '../users/groups/groups.service';
import { isNil } from 'lodash-es';
import { StoresService } from '../stores/services/stores.service';
import { sortByField } from '../shared/custom-rxjs-operators';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {

  constructor(
    private miClient: MIHttpClientService,
    private groupsService: GroupsService,
    private storesService: StoresService,
    private helpersService: HelpersService,
    public permissionService: PermissionService,
  ) {
  }

  getOOOTypes(): Observable<IOutOfOfficeType[]> {
    return this.miClient.get<IOutOfOfficeType[]>('outOfOfficeTypes').pipe(sortByField('name'));
  }

  createOutOfOfficeType(item: any): Observable<any> {
    return this.miClient.post('outOfOfficeTypes', item);
  }

  getOutOfOfficeTypeDetails(id: number): Observable<any> {
    return this.miClient.get<any>('outOfOfficeTypes/' + id);
  }

  updateOutOfOfficeType(item: any): Observable<any> {
    const id = +item.id;
    return this.miClient.post('outOfOfficeTypes/' + id, item);
  }

  deleteOutOfOfficeType(id: number): Observable<void> {
    return this.miClient.delete<void>('outOfOfficeTypes/' + id);
  }

  createOutOfOfficeEvent(item: any): Observable<any> {
    return this.miClient.post('outOfOffice', item);
  }

  updateOutOfOfficeEvent(item: any): Observable<any> {
    return this.miClient.post('outOfOffice/' + item.id, item);
  }

  deleteOutOfOffice(id: number): Observable<void> {
    return this.miClient.delete<void>('outOfOffice/' + id);
  }

  getOutOfOfficeDetailsData(id: number): Observable<any> {
    const ooo$ = this.miClient.get<any>('outOfOffice/' + id);
    const sortedOooTypes$ = this.getOOOTypes();
    return forkJoin([ooo$, sortedOooTypes$]);
  }

  getStoreVisitEventTypes(): Observable<IStoreVisitEventType[]> {
    return this.miClient.get<any>('storeVisitEventTypes');
  }

  getCalendarEventTypes(): Observable<{ list: ICalendarEventType[]; count: number }> {
    return this.miClient.get<any>('calendarEventTypes');
  }

  getCalendarEventType(id: number): Observable<ICalendarEventType> {
    return this.miClient.get('calendarEventTypes/' + id);
  }


  getStoreVisitEventData(username: string, id: number = null): Observable<any> {
      const request = [];
      request.push(this.miClient.get<any>('storeVisitEventTypes'));
      request.push(this.groupsService.getMyGroups());
      if(!isNil(id)) {
        request.push(this.miClient.get('scheduledVisitEntries/' + id));
      } else {
        // initial load shows my stores
        request.push(this.storesService.getMyStores(username));
      }
      return forkJoin(request);
  }

  getStoresForAccount(account: string): Observable<any> {
    return this.miClient.get<IStore[]>('stores/?accountName=' + account);
  }

  getStores(): Observable<any> {
    return this.miClient.get<IStore[]>('stores');
  }

  createCalendarEventType(item: ICalendarEventType): Observable<ICalendarEventType> {
    return this.miClient.post('calendarEventTypes', item);
  }

  updateCalendarEventType(item: ICalendarEventType): Observable<ICalendarEventType> {
    return this.miClient.post('calendarEventTypes/' + item.id, item);
  }


  deleteStoreVisitEventType(id: number): Observable<void> {
    return this.miClient.delete<void>('storeVisitEventTypes/' + id);
  }

  createScheduledVisit(scheduledVisit: IScheduledVisit): Observable<any> {
    return this.miClient.post('scheduledVisitEntries', scheduledVisit);
  }

  updateStoreVisitEvent(item: any): Observable<any> {
    return this.miClient.post('scheduledVisitEntries/' + item.id, item);
  }

  deleteStoreVisitEvent(id: number): Observable<void> {
    return this.miClient.delete<void>('scheduledVisitEntries/' + id);
  }

  checkCalendarEventTypesPermissions() {
    const activityPermission$ = this.permissionService.hasPermission('ACTIVITY').pipe(take(1));
    const storeVisitPermission$ = this.permissionService.hasPermission('STORE_VISIT_EVENT_TYPE_VIEW').pipe(take(1));
    const oooPermission$ = this.permissionService.hasPermission('OUT_OF_OFFICE_EVENT_VIEW').pipe(take(1));

    return forkJoin([activityPermission$, storeVisitPermission$, oooPermission$]);
  }

  checkGetEventsPermissions() {
    const activityPermission$ = this.permissionService.hasPermission('ACTIVITY').pipe(take(1));
    const storeVisitPermission$ = this.permissionService.hasPermission('CALENDAR_VIEW').pipe(take(1));
    const oooPermission$ = this.permissionService.hasPermission('OUT_OF_OFFICE_EVENT_VIEW').pipe(take(1));

    return forkJoin([activityPermission$, storeVisitPermission$, oooPermission$]);
  }

  getEvents(filters: ICalendarFilters): Observable<any[]> {

  return  this.checkGetEventsPermissions()
      .pipe(
        mergeMap(([jobPerm, storeVisitPerm, oooPerm]) => {
          const request = [];
          const emptyList = {pageNumber: 0, startIndex: 0,  pageSize: 0, list: [], TotalRowCount: 0};
          if (jobPerm) {
            request.push(this.miClient.get<IJob[]>(`activityItems${filters.jobs}&isScheduled=1&includeSubmittedActivities=true&isActive=true&pageSize=1000`));
          } else {
            request.push(of(emptyList));
          }
          if (storeVisitPerm) {
            request.push(this.miClient.get<IScheduledVisit[]>(`scheduledVisitEntries${filters.visits}` + '&retrieveAllMyEvents=true&pageSize=1000'));
          } else {
            request.push(of(emptyList));
          }
          if (oooPerm) {
            request.push(this.miClient.get<IOutOfOffice[]>(`outOfOffice${filters.outOfOffice}` + '&returnOverlapping=true&pageSize=1000'));
          } else {
            request.push(of(emptyList));
          }
          if (storeVisitPerm) {
            request.push(this.getStoreVisitEventTypes());
          } else {
            request.push(of(emptyList));
          }
          if (jobPerm || oooPerm) {
            request.push(this.miClient.get<ICalendarEventType[]>(`calendarEventTypes`));
          } else {
            request.push(of([]));
          }

          return forkJoin(request);
        }),
      );
  }

  transformOOOEventToMICalendarEvent(ooo: IOutOfOffice, timezone: string, colors: ICalendarEventType[] = []): MICalendarEvent {
    const startDate = ooo['startDate'];
    const endDate = ooo['endDate'];
    const ed = this.helpersService.createDateFromPartsInTz(endDate, timezone);
    const nextDay = moment(ed).add(1, 'days').format('YYYY-MM-DD');

    let eventColor = colors.filter(color => color.eventType.toLowerCase() === ooo.outOfOfficeType.toLowerCase())[0]?.eventColorCode;
    if(!eventColor?.includes("#")) {
      eventColor = '#' + eventColor;
    }

    const ev = {
      eventType: MICalendarEventTypesEnum.OutOfOfficeEvent,
      title: ooo.description + ' ' + ooo.outOfOfficeType + ' \n ' + ooo.displayName,
      allDay: true,
      oooDetails: ooo,
      eventId: ooo.id,
      textColor: 'white',
      borderColor: 'white',
      backgroundColor: eventColor,
      start: this.helpersService.createDateFromPartsInTz(startDate, timezone),
      end: nextDay,
    };

    return ev;
  }

  transformJobEventToMICalendarEvent(job: IJob, colors: ICalendarEventType[] = []): MICalendarEvent {
    const startDate = job['scheduledStartDateTime'] !== null ? +job['scheduledStartDateTime'] : null;
    const endDate = job['scheduledEndDateTime'] !== null ? +job['scheduledEndDateTime'] : null;
    const timezone = job.storeTimezone;
    const timeTxt = startDate !== null ? this.helpersService.extractTimeTimestampInTz(startDate, timezone) : null;


    let eventColor = colors.filter(color => color.eventType.toLowerCase() === job.status.toLowerCase())[0]?.eventColorCode;
    if(!eventColor?.includes("#")) {
      eventColor = '#' + eventColor;
    }

    const ev = {
      eventType: MICalendarEventTypesEnum.Job,
      title: job.description + ' \n ' + job.storeName,
      allDay: false,
      textColor: 'white',
      borderColor: 'white',
      backgroundColor: eventColor,
      display: 'block',
      jobDetails: job,
      eventId: job.id,
      timeText: timeTxt,
      start: this.helpersService.createDateFromTimestampInTz(startDate, timezone),
      end: this.helpersService.createDateFromTimestampInTz(endDate, timezone),
    };

    return ev;
  }

  transformStoreVisitToMICalendarEvent(storeVisit: IScheduledVisit, storeVisitEventTypes: IStoreVisitEventType[]): MICalendarEvent {

    const title = (storeVisit.storeNickname !== null ?
      storeVisit.storeNickname : storeVisit.storeName) + ' \n ' + storeVisit.storeVisitEventType;
    const startDate = +storeVisit['startTime'];
    const endDate = +storeVisit['endTime'];

    const timezone = storeVisit.timeZoneJavaId;
    const timeTxt = this.helpersService.extractTimeTimestampInTz(startDate, timezone);
    const backgroundColor = ('#' + storeVisitEventTypes.find(type => type.id === storeVisit.storeVisitEventTypeId)?.eventColorCode) || 'white';
    const ev = {
      eventType: MICalendarEventTypesEnum.StoreVisitsEvent,
      title: title,
      allDay: false,
      storeVisit: storeVisit,
      eventId: storeVisit.id,
      textColor: 'black',
      borderColor: 'white',
      backgroundColor: backgroundColor,
      display: 'block',
      timeText: timeTxt,
      start: this.helpersService.createDateFromTimestampInTz(startDate, timezone),
      end: this.helpersService.createDateFromTimestampInTz(endDate, timezone),
    };

    return ev;
  }

  transformVisitCounterRowToMICalendarEvent(row: any): MICalendarEvent {

    const title = (row.nickname !== null ? row.nickname : row.storeName);
    const ev = {
      eventType: MICalendarEventTypesEnum.StoreVisitsEvent,
      title: title,
      allDay: false,
      visitCounterRow: row,
      eventId: row.storeId,
      start: null,
      end: null,
    };

    return ev;
  }

  buildEventTypesArray(disableCreateStoreVisit: boolean): IdName[] {
    let eventTypes: IdName[] = [];
    this.permissionService.hasPermission('ACTIVITY_ADMIN').subscribe(response => {
      if (response) {
        eventTypes.push({id: MICalendarEventTypesEnum.Job, name: 'Job'});
      }
    } );
    this.permissionService.hasPermission('OUT_OF_OFFICE_EVENT_VIEW').subscribe(response => {
      if (response) {
        eventTypes.push({id: MICalendarEventTypesEnum.OutOfOfficeEvent, name: 'Out Of Office'});
      }
    } );
    this.permissionService.hasPermission('CALENDAR_MODIFY').subscribe(response => {
      if (response && !disableCreateStoreVisit) {
        eventTypes.push( {id: MICalendarEventTypesEnum.StoreVisitsEvent, name: 'Store Visit'});
      }
    } );
    return eventTypes;
  }
}
